import { PARENT_GUIDE_SLUG, PARENT_GUIDE_LAUNCH_READY } from "./parentGuide";

/**
 * Launch catalog — single source of truth for what the public sees.
 *
 * During controlled launch a small, curated set of pathways is enrolment-open
 * (BGMI Rank-Up Path and Free Fire Rank-Up Path). Everything else is
 * gated. A short "coming soon" list keeps the catalog credible without
 * advertising paths we can't deliver yet.
 */

/** Esports Educator (career track, shown under Esports careers on /learn). */
export const EDUCATOR_PATHWAY_SLUG = "esports-educator";

/**
 * Esports Educator launch switch.
 *
 * The Season (20-module spine) must be authored, practitioner-signed-off and
 * published before this flips. When it does, flip BOTH this flag and the
 * `pathways.enrollment_open` column for `esports-educator` — the DB trigger on
 * `learner_enrollments` is authoritative.
 */
// 2026-09-24 (owner): enrolment paused; pathway stays browsable with free
// preview + notify list via PREVIEW_OPEN_PATHWAY_SLUGS.
export const EDUCATOR_LAUNCH_READY = true;

/** Slugs of the pathways that are open for enrolment right now. */
// 2026-09-28 (owner): BGMI hidden from the public site — Free Fire is the only
// open player pathway. Existing BGMI buyers keep access via their enrolments.
// 2026-10-04 (owner): BGMI re-opened — free, India-only (regionalCatalog).
export const OPEN_PATHWAY_SLUGS: readonly string[] = [
  "free-fire-fundamentals",
  "bgmi-fundamentals",
  ...(EDUCATOR_LAUNCH_READY ? [EDUCATOR_PATHWAY_SLUG] : []),
  // 2026-10-03 (owner): Event Operations public release (published, enrolment open).
  "event-tournament-operations",
  ...(PARENT_GUIDE_LAUNCH_READY ? [PARENT_GUIDE_SLUG] : []),
];

/** Primary open pathway (used for the single-CTA marketing surfaces). */
export const OPEN_PATHWAY_SLUG = OPEN_PATHWAY_SLUGS[0];

export const isOpenPathwaySlug = (slug?: string | null) =>
  !!slug && OPEN_PATHWAY_SLUGS.includes(slug);

/**
 * Pathways that expose a free preview module even though enrolment is still
 * closed. Reading one module is a taste, not an enrolment — the DB keeps its
 * own pin (`pathway_free_preview`) and that row is authoritative for access.
 * Parent Guide has no anonymous preview, including after enrolment opens.
 * Other enrolment-open pathways retain their existing preview behavior.
 */
export const PREVIEW_OPEN_PATHWAY_SLUGS: readonly string[] = [EDUCATOR_PATHWAY_SLUG];

/** True when the pathway may show a free preview to anonymous visitors. */
export const isPreviewOpenPathwaySlug = (slug?: string | null) =>
  slug !== PARENT_GUIDE_SLUG && (isOpenPathwaySlug(slug) || (!!slug && PREVIEW_OPEN_PATHWAY_SLUGS.includes(slug)));

/** Human-readable names of the open pathways, for gate explanations. */
export const OPEN_PATHWAY_LABELS: Record<string, string> = {
  "bgmi-fundamentals": "BGMI Rank-Up Path",
  "free-fire-fundamentals": "Free Fire Rank-Up Path",
  [EDUCATOR_PATHWAY_SLUG]: "Esports Educator Path",
  "event-tournament-operations": "Esports Event & Tournament Operations",
  [PARENT_GUIDE_SLUG]: "A Parent’s Guide to Gaming & Digital Safety",
};

/**
 * Academy levels (L1–L3). Mirrors the canonical pathway ladder:
 * Foundational → Intermediate → Advanced.
 */
export const ACADEMY_LEVELS = [
  { code: "L1", name: "Foundational" },
  { code: "L2", name: "Intermediate" },
  { code: "L3", name: "Advanced" },
] as const;

export type AcademyLevelCode = (typeof ACADEMY_LEVELS)[number]["code"];

/** Academy level of each enrolment-open pathway. */
export const OPEN_PATHWAY_LEVELS: Record<string, AcademyLevelCode> = {
  "bgmi-fundamentals": "L1",
  "free-fire-fundamentals": "L1",
  // Esports Educator is a professional (WORK) track — Intermediate ladder slot.
  [EDUCATOR_PATHWAY_SLUG]: "L2",
  "event-tournament-operations": "L1",
};

/** Live pathway count per academy level, derived from the open catalog. */
export const LIVE_LEVEL_BREAKDOWN = ACADEMY_LEVELS.map((level) => ({
  ...level,
  count: OPEN_PATHWAY_SLUGS.filter((s) => OPEN_PATHWAY_LEVELS[s] === level.code).length,
}));


/** Label of the primary open pathway. */
export const OPEN_PATHWAY_LABEL = OPEN_PATHWAY_LABELS[OPEN_PATHWAY_SLUG];

/** "BGMI Rank-Up Path and Free Fire Rank-Up Path" */
export const OPEN_PATHWAY_LABEL_LIST = OPEN_PATHWAY_SLUGS.map(
  (s) => OPEN_PATHWAY_LABELS[s],
).reduce((acc, label, i, arr) =>
  i === 0 ? label : i === arr.length - 1 ? `${acc} and ${label}` : `${acc}, ${label}`,
"");

/**
 * Catalog / decide CTA target for an open pathway (Holistic P6.1).
 * Prefer pathway detail — not the `/bgmi` marketing landing — so learners
 * reach price/preview/notify in one click. `/bgmi` stays for SEO landings.
 */
export const getOpenPathwayHref = (slug: string) => `/learn/${slug}`;

/** In-app shell equivalent of {@link getOpenPathwayHref}. */
export const getOpenPathwayAppHref = (slug: string) => `/app/pathways/${slug}`;

/**
 * Curated coming-soon paths. Deliberately short: entry-level, standalone,
 * and genuinely next in the roadmap. Ladder continuations (Intermediate /
 * Professional) and back-office roles are excluded.
 *
 * Esports Educator is preview-open and priced, not coming soon. Enrolment
 * stays closed until EDUCATOR_LAUNCH_READY flips.
 */
export const COMING_SOON_SLUGS: readonly string[] = [
  "esports-broadcast-producer",
  "community-operations",
  "roblox-creator",
];

/**
 * Pathways actually in production right now. They are still draft in the
 * database (no content, price, preview or enrolment), so the public catalog
 * cannot render them from data — this short static list is what visitors see
 * under "Coming soon". Keep it to work genuinely in the pipeline.
 */
export const PIPELINE_PATHWAYS: readonly {
  slug: string;
  title: string;
  blurb: string;
}[] = [
  {
    slug: "esports-broadcast-producer",
    title: "Esports Broadcast Producer",
    blurb: "Run a live show: rundowns, observing, replays and a calm gallery.",
  },
  {
    slug: "community-operations",
    title: "Community Operations",
    blurb: "Build and moderate a healthy player community, safely and at scale.",
  },
  {
    slug: "roblox-creator",
    title: "Roblox Creator",
    blurb: "Design, build and publish experiences players actually come back to.",
  },
];

export const isComingSoonSlug = (slug: string) =>
  (COMING_SOON_SLUGS as readonly string[]).includes(slug);

/**
 * Pathways visitors can explore on the catalog (enrolment-open, preview-open,
 * or founding-eligible). Used so a priced preview is not treated as Coming soon.
 */
export function isCatalogExploreOpen(p: {
  slug?: string | null;
  founding_eligible?: boolean | null;
}): boolean {
  return isOpenPathwaySlug(p.slug) || !!p.founding_eligible || isPreviewOpenPathwaySlug(p.slug);
}

/**
 * Learner-facing names for every pathway a visitor can join the notify list
 * for — the launch pathways plus the curated coming-soon paths.
 */
export const NOTIFIABLE_PATHWAY_LABELS: Record<string, string> = {
  ...OPEN_PATHWAY_LABELS,
  "esports-broadcast-producer": "Esports Broadcast Producer",
  "community-operations": "Community Operations",
  "roblox-creator": "Roblox Creator",
};

/**
 * Short, learner-facing explanation of why a pathway is locked.
 * Keep to one line — it renders inline on catalog rows and tiles.
 */
export function getGateReason(p: {
  slug: string;
  founding_eligible?: boolean | null;
  course_count?: number;
  module_count?: number;
}): string | null {
  if (isOpenPathwaySlug(p.slug)) return null;
  if (p.founding_eligible) return null;
  if (isPreviewOpenPathwaySlug(p.slug)) return null;
  if (isComingSoonSlug(p.slug)) {
    if ((p.course_count ?? 1) === 0 || (p.module_count ?? 1) === 0) {
      return "Locked while lessons are still being prepared. Join the notify list and we'll email you when it opens.";
    }
    return "Locked during our staged launch. Join the notify list and we'll email you when it opens.";
  }
  return "Locked during our staged launch. Join the notify list and we'll email you when it opens.";
}
